/**
 * Groups announcements into the per-BU sections the What's New list renders.
 *
 * @module Pages/BUs/WhatsNew
 */

import type { WhatsNew } from '../Types/WhatsNew.types';
import { WhatsNewApprovalStatus } from '../Types/WhatsNew.types';
import { compareTenantSlugs } from '@/Shared/brand/tenantOrder';

/** One BU's section of announcements. */
export interface TenantWhatsNewGroup {
  tenantSlug: string;
  items: WhatsNew[];
  /** e.g. "4 anúncios". */
  countLabel: string;
  /** e.g. "2 aguardando", or empty when none are pending. */
  pendingLabel: string;
  /** Pending items draw attention; zero should not. */
  hasPending: boolean;
}

/**
 * Groups by tenant, tenants ordered by compareTenantSlugs and items newest first inside each — an approval queue
 * is read top-down, and the most recent submission is the one people look for.
 */
export function groupWhatsNewByTenant(items: WhatsNew[]): TenantWhatsNewGroup[] {
  const byTenant = new Map<string, WhatsNew[]>();

  for (const item of items) {
    const existing = byTenant.get(item.tenantSlug);
    if (existing) {
      existing.push(item);
    } else {
      byTenant.set(item.tenantSlug, [item]);
    }
  }

  return [...byTenant.entries()]
    .sort(([a], [b]) => compareTenantSlugs(a, b))
    .map(([tenantSlug, tenantItems]) => {
      const ordered = [...tenantItems].sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt).getTime() -
          new Date(a.updatedAt || a.createdAt).getTime(),
      );
      const pendingCount = ordered.filter(
        item => item.approvalStatus === WhatsNewApprovalStatus.PENDING_APPROVAL,
      ).length;

      return {
        tenantSlug,
        items: ordered,
        countLabel: `${ordered.length} ${ordered.length === 1 ? 'anúncio' : 'anúncios'}`,
        pendingLabel: pendingCount > 0 ? `${pendingCount} aguardando` : '',
        hasPending: pendingCount > 0,
      };
    });
}
